import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { MultiSelect } from '../components/MultiSelect';
import './AdvertiserForm.css';

interface Affiliate {
  id: number;
  name: string;
  company: string;
  email: string;
  status: 'pending' | 'approved' | 'blocked';
}

const ViewAdvertiser: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [advertiserData, setAdvertiserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [allAffiliates, setAllAffiliates] = useState<Affiliate[]>([]);
  const [selectedApprovedAffiliates, setSelectedApprovedAffiliates] = useState<number[]>([]);
  const [selectedBlockedAffiliates, setSelectedBlockedAffiliates] = useState<number[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);

        // Fetch advertiser details
        const advertiserResponse = await apiService.getAdvertiser(parseInt(id));
        const advertiser = advertiserResponse.advertiser;

        // Parse billing_contact if it's a JSON object
        const billingContact = typeof advertiser.billing_contact === 'object'
          ? advertiser.billing_contact
          : (typeof advertiser.billing_contact === 'string' && advertiser.billing_contact
            ? JSON.parse(advertiser.billing_contact)
            : {});

        setAdvertiserData({
          id: String(advertiser.id || ''),
          name: String(advertiser.name || ''),
          contact_email: String(advertiser.contact_email || ''),
          billing_first_name: billingContact.first_name || '',
          billing_last_name: billingContact.last_name || '',
          billing_phone: billingContact.phone || '',
          billing_address: billingContact.address || '',
          billing_city: billingContact.city || '',
          billing_state: billingContact.state || '',
          billing_country: billingContact.country || '',
          status: String(advertiser.status || 'active'),
          monthly_budget: Number(advertiser.monthly_budget) || 0,
          budget_alert_threshold: Number(advertiser.budget_alert_threshold) || 80,
          secret_key: String(advertiser.secret_key || ''),
          postback_auth_method: String(advertiser.postback_auth_method || 'hmac'),
          postback_url_template: String(advertiser.postback_url_template || ''),
          whitelisted_ips: Array.isArray(advertiser.whitelisted_ips) ? advertiser.whitelisted_ips : [],
          created_at: advertiser.created_at,
          updated_at: advertiser.updated_at
        });

        // Fetch publishers with approval status for this advertiser
        const publishersResponse = await apiService.getAdvertiserPublishers(parseInt(id));

        // Map publishers to affiliates format with approval status
        const affiliates = publishersResponse.publishers.map((publisher: any) => ({
          id: publisher.id,
          name: publisher.name || '',
          company: publisher.name || '',
          email: publisher.email || '',
          status: publisher.approval_status || 'blocked'
        }));
        setAllAffiliates(affiliates);

      } catch (err: any) {
        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const [affiliateStatuses, setAffiliateStatuses] = useState<Record<number, 'approved' | 'blocked'>>({});

  // Update affiliate statuses when allAffiliates changes
  useEffect(() => {
    setAffiliateStatuses(
      Object.fromEntries(allAffiliates.map(affiliate => [affiliate.id, affiliate.status as 'approved' | 'blocked']))
    );
  }, [allAffiliates]);

  const approvedAffiliates = allAffiliates.filter(affiliate => affiliateStatuses[affiliate.id] === 'approved');
  const blockedAffiliates = allAffiliates.filter(affiliate => affiliateStatuses[affiliate.id] === 'blocked');

  const approvedAffiliateOptions = approvedAffiliates.map((affiliate) => ({
    id: affiliate.id,
    name: `${affiliate.name}${affiliate.email ? ` (${affiliate.email})` : ''}`
  }));

  const blockedAffiliateOptions = blockedAffiliates.map((affiliate) => ({
    id: affiliate.id,
    name: `${affiliate.name}${affiliate.email ? ` (${affiliate.email})` : ''}`
  }));

  const updateAffiliateStatus = async (affiliateIds: number[], newStatus: 'approved' | 'blocked') => {
    if (!id || affiliateIds.length === 0) return;

    try {
      for (const affiliateId of affiliateIds) {
        if (newStatus === 'approved') {
          await apiService.approvePublisherForAdvertiser(parseInt(id), affiliateId);
        } else {
          await apiService.blockPublisherForAdvertiser(parseInt(id), affiliateId);
        }
      }

      setAffiliateStatuses((prev) => {
        const next = { ...prev };
        affiliateIds.forEach((affiliateId) => {
          next[affiliateId] = newStatus;
        });
        return next;
      });

      setSelectedApprovedAffiliates((prev) => prev.filter((affiliateId) => !affiliateIds.includes(affiliateId)));
      setSelectedBlockedAffiliates((prev) => prev.filter((affiliateId) => !affiliateIds.includes(affiliateId)));

      alert(
        `${affiliateIds.length} publisher${affiliateIds.length === 1 ? '' : 's'} ${
          newStatus === 'approved' ? 'approved' : 'blocked'
        } successfully!`
      );
    } catch (err: any) {
      alert(`Failed to update publisher status: ${err.message}`);
    }
  };

  const toggleAffiliateStatus = async (affiliateId: number) => {
    const currentStatus = affiliateStatuses[affiliateId];
    const newStatus = currentStatus === 'approved' ? 'blocked' : 'approved';
    await updateAffiliateStatus([affiliateId], newStatus);
  };

  const handleEdit = () => {
    navigate(`/advertisers/edit/${id}`);
  };

  const handleBack = () => {
    navigate('/advertisers');
  };

  if (loading) {
    return (
      <div className="advertiser-view-page">
        <div className="loading">Loading advertiser details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="advertiser-view-page">
        <div className="error">Error: {error}</div>
        <button className="btn btn-secondary" onClick={handleBack}>
          Back to Advertisers
        </button>
      </div>
    );
  }

  if (!advertiserData) {
    return (
      <div className="advertiser-view-page">
        <div className="error">Advertiser not found</div>
        <button className="btn btn-secondary" onClick={handleBack}>
          Back to Advertisers
        </button>
      </div>
    );
  }

  return (
    <div className="advertiser-view-page">
      <div className="view-header">
        <h1>Advertiser Details</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleEdit}>
            Edit
          </button>
          <button className="btn btn-warning" onClick={handleBack}>
            Back
          </button>
        </div>
      </div>

      <div className="view-form-container">
        <table className="advertiser-details-table">
          <tbody>
            <tr>
              <td className="form-label">ID</td>
              <td className="form-value">{advertiserData.id}</td>
            </tr>

            <tr>
              <td className="form-label">Name</td>
              <td className="form-value">{advertiserData.name}</td>
            </tr>

            <tr>
              <td className="form-label">Contact Email</td>
              <td className="form-value">{advertiserData.contact_email}</td>
            </tr>

            <tr>
              <td className="form-label">Billing Contact Name</td>
              <td className="form-value">
                {advertiserData.billing_first_name || advertiserData.billing_last_name
                  ? `${advertiserData.billing_first_name} ${advertiserData.billing_last_name}`.trim()
                  : 'Not specified'}
              </td>
            </tr>

            <tr>
              <td className="form-label">Billing Phone</td>
              <td className="form-value">{advertiserData.billing_phone || 'Not specified'}</td>
            </tr>

            <tr>
              <td className="form-label">Billing Address</td>
              <td className="form-value">
                {advertiserData.billing_address || advertiserData.billing_city || advertiserData.billing_state || advertiserData.billing_country ? (
                  <div>
                    {advertiserData.billing_address && <div>{advertiserData.billing_address}</div>}
                    {(advertiserData.billing_city || advertiserData.billing_state) && (
                      <div>
                        {[advertiserData.billing_city, advertiserData.billing_state].filter(Boolean).join(', ')}
                      </div>
                    )}
                    {advertiserData.billing_country && <div>{advertiserData.billing_country}</div>}
                  </div>
                ) : 'Not specified'}
              </td>
            </tr>

            <tr>
              <td className="form-label">Status</td>
              <td className="form-value">
                <span className={`status-badge ${advertiserData.status === 'active' ? 'success' : 'warning'}`}>
                  {advertiserData.status.charAt(0).toUpperCase() + advertiserData.status.slice(1)}
                </span>
              </td>
            </tr>

            <tr>
              <td className="form-label">Monthly Budget</td>
              <td className="form-value">${typeof advertiserData.monthly_budget === 'number' ? advertiserData.monthly_budget.toFixed(2) : '0.00'}</td>
            </tr>

            <tr>
              <td className="form-label">Budget Alert Threshold</td>
              <td className="form-value">{advertiserData.budget_alert_threshold}%</td>
            </tr>

            <tr>
              <td className="form-label">Secret Key</td>
              <td className="form-value">
                <span style={{ fontFamily: 'monospace', backgroundColor: '#4a5568', padding: '4px 8px', borderRadius: '3px' }}>
                  {advertiserData.secret_key || 'Not set'}
                </span>
              </td>
            </tr>

            <tr>
              <td className="form-label">Postback Auth Method</td>
              <td className="form-value">{advertiserData.postback_auth_method?.toUpperCase()}</td>
            </tr>

            <tr>
              <td className="form-label">Postback URL Template</td>
              <td className="form-value">
                <code style={{ fontSize: '12px', backgroundColor: '#4a5568', padding: '4px 8px', borderRadius: '3px' }}>
                  {advertiserData.postback_url_template || 'Not configured'}
                </code>
              </td>
            </tr>

            <tr>
              <td className="form-label">Whitelisted IPs</td>
              <td className="form-value">
                {advertiserData.whitelisted_ips && advertiserData.whitelisted_ips.length > 0
                  ? advertiserData.whitelisted_ips.join(', ')
                  : 'No IP restrictions'
                }
              </td>
            </tr>

            <tr>
              <td className="form-label">Created</td>
              <td className="form-value">
                {advertiserData.created_at ? new Date(advertiserData.created_at).toLocaleDateString() : 'Not available'}
              </td>
            </tr>

            <tr>
              <td className="form-label">Last Updated</td>
              <td className="form-value">
                {advertiserData.updated_at ? new Date(advertiserData.updated_at).toLocaleString() : 'Not available'}
              </td>
            </tr>

            <tr>
              <td className="form-label" style={{ verticalAlign: 'top', paddingTop: '20px' }}>Approved Affiliates</td>
              <td className="form-value">
                <div className="affiliate-section">
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => updateAffiliateStatus(approvedAffiliates.map((affiliate) => affiliate.id), 'blocked')}
                      disabled={approvedAffiliates.length === 0}
                    >
                      Block All ({approvedAffiliates.length})
                    </button>
                    {selectedApprovedAffiliates.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => updateAffiliateStatus(selectedApprovedAffiliates, 'blocked')}
                      >
                        Block Selected ({selectedApprovedAffiliates.length})
                      </button>
                    )}
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <MultiSelect
                      label={`Approved Affiliates (${approvedAffiliates.length})`}
                      options={approvedAffiliateOptions}
                      value={selectedApprovedAffiliates}
                      onChange={(selected) => setSelectedApprovedAffiliates(selected.map((affiliateId) => Number(affiliateId)))}
                      placeholder="Select approved affiliates..."
                    />
                  </div>
                  <div className="affiliate-list" style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #5a6c7d', borderRadius: '4px', padding: '10px' }}>
                    {approvedAffiliates.length === 0 ? (
                      <div style={{ color: '#a0aec0', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                        No approved affiliates
                      </div>
                    ) : (
                      approvedAffiliates.map((affiliate) => (
                        <div key={affiliate.id} className="affiliate-item" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 10px',
                          borderBottom: '1px solid #5a6c7d',
                          backgroundColor: '#4a5568',
                          marginBottom: '5px',
                          borderRadius: '3px'
                        }}>
                          <div>
                            <div style={{ color: '#ecf0f1', fontSize: '14px', fontWeight: '500' }}>{affiliate.name}</div>
                            <div style={{ color: '#a0aec0', fontSize: '12px' }}>{affiliate.email}</div>
                          </div>
                          <button
                            onClick={() => toggleAffiliateStatus(affiliate.id)}
                            className="btn btn-danger btn-sm"
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            Block
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </td>
            </tr>

            <tr>
              <td className="form-label" style={{ verticalAlign: 'top', paddingTop: '20px' }}>Blocked Affiliates</td>
              <td className="form-value">
                <div className="affiliate-section">
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => updateAffiliateStatus(blockedAffiliates.map((affiliate) => affiliate.id), 'approved')}
                      disabled={blockedAffiliates.length === 0}
                    >
                      Approve All ({blockedAffiliates.length})
                    </button>
                    {selectedBlockedAffiliates.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={() => updateAffiliateStatus(selectedBlockedAffiliates, 'approved')}
                      >
                        Approve Selected ({selectedBlockedAffiliates.length})
                      </button>
                    )}
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <MultiSelect
                      label={`Blocked Affiliates (${blockedAffiliates.length})`}
                      options={blockedAffiliateOptions}
                      value={selectedBlockedAffiliates}
                      onChange={(selected) => setSelectedBlockedAffiliates(selected.map((affiliateId) => Number(affiliateId)))}
                      placeholder="Select blocked affiliates..."
                    />
                  </div>
                  <div className="affiliate-list" style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #5a6c7d', borderRadius: '4px', padding: '10px' }}>
                    {blockedAffiliates.length === 0 ? (
                      <div style={{ color: '#a0aec0', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                        No blocked affiliates
                      </div>
                    ) : (
                      blockedAffiliates.map((affiliate) => (
                        <div key={affiliate.id} className="affiliate-item" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 10px',
                          borderBottom: '1px solid #5a6c7d',
                          backgroundColor: '#4a5568',
                          marginBottom: '5px',
                          borderRadius: '3px'
                        }}>
                          <div>
                            <div style={{ color: '#ecf0f1', fontSize: '14px', fontWeight: '500' }}>{affiliate.name}</div>
                            <div style={{ color: '#a0aec0', fontSize: '12px' }}>{affiliate.email}</div>
                          </div>
                          <button
                            onClick={() => toggleAffiliateStatus(affiliate.id)}
                            className="btn btn-success btn-sm"
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            Approve
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ViewAdvertiser;
